/**
 * TaskDurationInfo - Campo de duração efetiva na sidebar + modal explicativo
 * 
 * Exibe o tempo de trabalho efetivo (excluindo pausas) com atualização em tempo real.
 * Inclui ícone "?" que abre modal explicativo com detalhes da fórmula e dados reais.
 * 
 * @version 1.0.0
 * RELEASE: 2026-04-11 v1.0.0 - Criação inicial
 */

import { useState, useEffect, useMemo } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Timer, HelpCircle, Clock, PauseCircle, Play, CheckCircle, XCircle, Minus } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import type { TaskDetail } from '@/hooks/useTaskDetails';

/** Props do componente */
interface TaskDurationInfoProps {
  /** Dados completos da tarefa */
  task: TaskDetail;
}

/**
 * Formata milissegundos para exibição legível
 * Exemplos: "2h 35m", "45m", "1d 3h", "< 1m"
 */
function formatDuration(ms: number): string {
  if (ms < 0) ms = 0;
  const totalSeconds = Math.floor(ms / 1000);
  if (totalSeconds < 60) return '< 1m';

  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  return `${minutes}m`;
}

/**
 * Formata milissegundos para HH:MM:SS
 */
function formatTimerFull(ms: number): string {
  if (ms < 0) ms = 0;
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

/**
 * Calcula os tempos da tarefa em milissegundos
 *
 * v1.1.0 (2026-08-29) — blindagem do cálculo:
 *  - a "pausa ativa" só é contabilizada quando a tarefa está realmente pausada
 *    (antes, uma pausa residual não encerrada crescia indefinidamente e zerava
 *    o tempo efetivo de tarefas já concluídas);
 *  - o tempo de pausa é limitado ao intervalo real da tarefa;
 *  - se o dado histórico ainda estiver inconsistente (pausa > total), o tempo
 *    efetivo passa a refletir o tempo total em vez de exibir zero.
 */
function calcTimes(task: TaskDetail, now: number) {
  if (!task.started_at) return { total: 0, pauses: 0, effective: 0 };

  const started = new Date(task.started_at).getTime();
  const end = task.completed_at
    ? new Date(task.completed_at).getTime()
    : task.cancelled_at
      ? new Date(task.cancelled_at).getTime()
      : now;

  const total = Math.max(0, end - started);

  // Pausas finalizadas (vindas do banco) + pausa ativa somente quando pausada
  const finishedPauses = (task.total_pause_duration_seconds || 0) * 1000;
  const activePause =
    task.status === 'paused' && task.current_pause_started_at
      ? Math.max(0, now - new Date(task.current_pause_started_at).getTime())
      : 0;

  const rawPauses = finishedPauses + activePause;

  // Dado inconsistente (pausa maior que o tempo total da tarefa): ignora a pausa
  // e mostra o tempo total como efetivo, em vez de exibir zero.
  const pauses = rawPauses > total ? 0 : rawPauses;

  const effective = Math.max(0, total - pauses);


  return { total, pauses, effective };
}


export function TaskDurationInfo({ task }: TaskDurationInfoProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [now, setNow] = useState(Date.now());

  // Atualiza a cada segundo quando in_progress (não pausada) ou pausada (para pausa ativa)
  const isLive = task.status === 'in_progress' || task.status === 'paused';

  useEffect(() => {
    if (!isLive || !task.started_at) return;
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [isLive, task.started_at]);

  // Só exibe se tarefa já foi iniciada
  if (!task.started_at) return null;

  const times = calcTimes(task, now);

  return (
    <>
      {/* Campo na sidebar */}
      <div className="flex items-center gap-2 py-1.5 w-full">
        <Timer className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        <span className="text-xs text-muted-foreground shrink-0">Duração</span>
        <div className="flex items-center gap-1.5 ml-auto">
          <button
            onClick={() => setModalOpen(true)}
            className={cn(
              'text-xs font-semibold cursor-pointer hover:underline transition-colors',
              task.status === 'in_progress' && !task.current_pause_started_at
                ? 'text-blue-600 dark:text-blue-400'
                : task.current_pause_started_at
                  ? 'text-amber-600 dark:text-amber-400'
                  : 'text-foreground'
            )}
            title="Clique para ver detalhes da duração"
          >
            {formatDuration(times.effective)}
          </button>
          <button
            onClick={() => setModalOpen(true)}
            className="text-muted-foreground hover:text-foreground transition-colors"
            title="Ver explicação da duração"
          >
            <HelpCircle className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Modal explicativo */}
      <DurationExplainerModal
        open={modalOpen}
        onOpenChange={setModalOpen}
        task={task}
        times={times}
      />
    </>
  );
}

// =========================================================================
// Modal explicativo
// =========================================================================

interface DurationExplainerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: TaskDetail;
  times: { total: number; pauses: number; effective: number };
}

function DurationExplainerModal({ open, onOpenChange, task, times }: DurationExplainerModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Timer className="h-5 w-5 text-primary" />
            Duração da Tarefa
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Seção 1: Fórmula visual */}
          <div className="rounded-lg border bg-muted/30 p-4">
            <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Tempo Total vs Tempo Efetivo
            </h5>
            <p className="text-xs text-muted-foreground mb-3">
              O tempo efetivo desconta todos os períodos em que a tarefa esteve pausada, 
              mostrando apenas o tempo real de trabalho.
            </p>
            {/* Fórmula visual */}
            <div className="flex items-center justify-center gap-2 py-2">
              <div className="text-center">
                <div className="text-xs text-muted-foreground mb-1">Tempo Total</div>
                <div className="text-sm font-mono font-semibold bg-background border rounded px-3 py-1.5">
                  {formatTimerFull(times.total)}
                </div>
              </div>
              <Minus className="h-4 w-4 text-muted-foreground mt-4" />
              <div className="text-center">
                <div className="text-xs text-muted-foreground mb-1">Pausas</div>
                <div className="text-sm font-mono font-semibold bg-amber-500/10 text-amber-700 dark:text-amber-400 border border-amber-500/30 rounded px-3 py-1.5">
                  {formatTimerFull(times.pauses)}
                </div>
              </div>
              <span className="text-muted-foreground font-bold mt-4">=</span>
              <div className="text-center">
                <div className="text-xs text-muted-foreground mb-1">Efetivo</div>
                <div className="text-sm font-mono font-semibold bg-blue-500/10 text-blue-700 dark:text-blue-400 border border-blue-500/30 rounded px-3 py-1.5">
                  {formatTimerFull(times.effective)}
                </div>
              </div>
            </div>
          </div>

          {/* Seção 2: Dados desta tarefa */}
          <div className="rounded-lg border p-4 space-y-2.5">
            <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Dados desta Tarefa
            </h5>

            {/* Iniciada em */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Play className="h-3.5 w-3.5 text-green-600" />
                <span className="text-xs text-muted-foreground">Iniciada em</span>
              </div>
              <span className="text-xs font-medium">
                {task.started_at
                  ? format(new Date(task.started_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
                  : '—'}
              </span>
            </div>

            {/* Concluída em (se aplicável) */}
            {task.completed_at && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle className="h-3.5 w-3.5 text-green-600" />
                  <span className="text-xs text-muted-foreground">Concluída em</span>
                </div>
                <span className="text-xs font-medium">
                  {format(new Date(task.completed_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </span>
              </div>
            )}

            {/* Cancelada em (se aplicável) */}
            {task.cancelled_at && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <XCircle className="h-3.5 w-3.5 text-red-600" />
                  <span className="text-xs text-muted-foreground">Cancelada em</span>
                </div>
                <span className="text-xs font-medium">
                  {format(new Date(task.cancelled_at), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })}
                </span>
              </div>
            )}

            {/* Total de pausas */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <PauseCircle className="h-3.5 w-3.5 text-amber-600" />
                <span className="text-xs text-muted-foreground">Total de pausas</span>
              </div>
              <span className="text-xs font-medium">
                {task.pauses_count || 0} {(task.pauses_count || 0) === 1 ? 'pausa' : 'pausas'}
                {times.pauses > 0 && (
                  <span className="text-muted-foreground ml-1">({formatDuration(times.pauses)})</span>
                )}
              </span>
            </div>

            {/* Tempo efetivo */}
            <div className="flex items-center justify-between pt-2 border-t">
              <div className="flex items-center gap-2">
                <Timer className="h-3.5 w-3.5 text-blue-600" />
                <span className="text-xs font-semibold">Tempo Efetivo</span>
              </div>
              <span className="text-xs font-bold text-blue-600 dark:text-blue-400">
                {formatDuration(times.effective)}
              </span>
            </div>
          </div>

          {/* Seção 3: Cenários de status */}
          <div className="rounded-lg border p-4">
            <h5 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
              Cenários de Status
            </h5>
            <div className="space-y-2">
              <StatusScenario
                icon={Play}
                iconColor="text-blue-600"
                title="Andamento"
                description="Cronômetro contando em tempo real"
              />
              <StatusScenario
                icon={PauseCircle}
                iconColor="text-amber-600"
                title="Pausada"
                description="Cronômetro congelado; tempo de pausa não conta"
              />
              <StatusScenario
                icon={CheckCircle}
                iconColor="text-green-600"
                title="Concluída / Cancelada"
                description="Cronômetro parado no momento do encerramento"
              />
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/** Linha de cenário de status */
function StatusScenario({ icon: Icon, iconColor, title, description }: {
  icon: typeof Play;
  iconColor: string;
  title: string;
  description: string;
}) {
  return (
    <div className="flex items-start gap-2">
      <Icon className={cn('h-3.5 w-3.5 mt-0.5 shrink-0', iconColor)} />
      <div>
        <span className="text-xs font-medium">{title}</span>
        <p className="text-[11px] text-muted-foreground">{description}</p>
      </div>
    </div>
  );
}
